import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Container,
    Grid,
    Stack,
    Typography,
    TextField,
    InputAdornment,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    SelectChangeEvent,
} from '@mui/material';
import { CoverImageRenderer } from '../../AuditionComponents/CoverImageRenderer';
import RoleRenderer from '../../AuditionComponents/RoleRenderer';
import { theme } from '../../../constants/theme';
import { useThemeContext } from '../../../hook/useThemeManager';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import FilterListIcon from '@mui/icons-material/FilterList';
import { CustomPagination } from '../../Tables/Pagination';
import { IPagination } from '../../../hook/usePagination';
import { IAudition, IRole, IRoleCreator } from '../../../types/global/helper';
import CustomButton from '../../Button/CustomButton';
import { ApplicationCard } from '../../ApplicationCard/ApplicationCard';
import { IApplication } from '../../../types/global/ListRender.types';
import _ from 'lodash';
import close from '../../../asset/images/icons/es_x_close.svg';
import SegmentedControl from '../../SegmentedControl/SegmentedControl';
import { AuditionDetailsTab } from './Tabs/AuditionDetailsTab';
import { AuditionAnalyticsTab } from './Tabs/AuditionAnalyticsTab';
import { AuditionPromotionsTab } from './Tabs/AuditionPromotionsTab';
import { RoleManagementDialog } from './Dialogs/RoleManagementDialog';
import { DialogueSheet } from '../../DialogueSheet/DialogueSheet';
import { useDispatch } from 'react-redux';
import {
    deleteAudition,
    createRole,
    updateRole,
    publishAudition,
    unPublishAudition,
} from '../../../redux/audition/actions';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FormOption } from '../../../types/global/forms.types';
import { CustomizedSwitches } from '../../Switch/Switch';
import { LinkBox } from '../../LinkBox';
import { generateShareLink } from '../../../utils/AppUtils';

// Define Application type that's compatible with IApplicationData
export interface TApplication {
    _id: string;
    user?: {
        profile_pic?: string;
        firstName?: string;
        lastName?: string;
    };
    role?: {
        character?: string;
        _id?: string;
    };
    status?: string;
    createdAt: string;
    total?: number;
    audition?: any;
    filing_status?: string;
    created_at?: string;
    // Add other properties as needed
}

// Styled FormControl for dropdowns
const StyledFormControl = styled(FormControl)(({ theme }) => ({
    minWidth: 120,
    margin: '0 8px',
}));

// Status options
const STATUS_OPTIONS = [
    { value: 'all', label: 'All Status' },
    { value: 'unfiled', label: 'Unfiled' },
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'archived', label: 'Archived' },
    { value: 'invited', label: 'Invited' },
    { value: 'cast', label: 'Cast' },
];

// Sort options
const SORT_OPTIONS = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'name_asc', label: 'Name (A-Z)' },
    { value: 'name_desc', label: 'Name (Z-A)' },
];

export interface IManageAuditionContainerProps {
    audition: IAudition | null;
    applications: IApplication[];
    loading: boolean;
    error: string | null;
    pagination: IPagination;
    filterStatus: string;
    sortOption: string;
    roleFilter: string;
    onSearchChange: (searchText: string) => void;
    onClearSearch: VoidFunction;
    onFilterStatusChange: (status: string) => void;
    onSortOptionChange: (option: string) => void;
    onRoleFilterChange: (roleId: string) => void;
    onRowsChange: (rows: number) => void;
    onPageChange: (page: number) => void;
    onEditRolesClick: () => void;
    onViewDetailsClick: (application: IApplication) => void;
    onAcceptApplication?: (application: IApplication) => void;
    onRejectApplication?: (application: IApplication) => void;
    onMessageApplicant?: (application: IApplication) => void;
    onArchiveApplication?: (application: IApplication) => void;
    onCastApplicant?: (application: IApplication) => void;
    onImageChange?: (image: string, rawFile: File) => void;
    searchLoading?: boolean;
}

const ManageAuditionContainer: React.FC<IManageAuditionContainerProps> = ({
    audition,
    applications,
    loading,
    error,
    pagination,
    filterStatus,
    sortOption,
    roleFilter,
    onSearchChange,
    onClearSearch,
    onFilterStatusChange,
    onSortOptionChange,
    onRoleFilterChange,
    onRowsChange,
    onPageChange,
    onEditRolesClick,
    onViewDetailsClick,
    onAcceptApplication,
    onRejectApplication,
    onMessageApplicant,
    onArchiveApplication,
    onCastApplicant,
    searchLoading,
    onImageChange,
}) => {
    const {
        themeStyle,
        device: { isMobile },
    } = useThemeContext();
    const [value, setValue] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'applications');
    const [roleDialogOpen, setRoleDialogOpen] = useState(false);
    const [selectedRole, setSelectedRole] = useState<IRoleCreator | undefined>(undefined);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [publishDialogOpen, setPublishDialogOpen] = useState(false);
    const [unpublishDialogOpen, setUnpublishDialogOpen] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Create an improved debounced search handler that handles all cases
    const debouncedSearchHandler = useRef(
        _.debounce((searchText: string) => {
            if (onSearchChange) {
                // Simulating a Promise-based onSearchChange operation
                Promise.resolve(onSearchChange(searchText))
                    .catch(error => console.error('Search error:', error))
                    .finally(() => {});
            }
        }, 500),
    ).current;

    // Handle input change with proper debouncing
    const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setValue(newValue);
        debouncedSearchHandler(newValue);
    };

    // Handle clear button click
    const handleClearSearch = () => {
        setValue('');
        onClearSearch && onClearSearch();
        // Cancel any pending debounced searches
        debouncedSearchHandler.cancel();
    };

    // Clean up the debounced function on unmount
    useEffect(() => {
        return () => {
            debouncedSearchHandler.cancel();
        };
    }, [debouncedSearchHandler]);

    // Extract unique roles for role filter dropdown
    const roleOptions = useMemo(() => {
        if (!audition || !audition.roles) return [];

        const roles = audition.roles
            .filter(role => role !== null)
            .map(role => ({
                id: role._id,
                name: role.character,
            }));

        // Add "All Roles" option
        return [{ id: 'all', name: 'All Roles' }, ...roles];
    }, [audition]);

    // Handle dropdown changes
    const handleStatusChange = (event: SelectChangeEvent<string>) => {
        onFilterStatusChange(event.target.value);
    };

    const handlePublishStatusChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const applicationCount = applications?.length || 0;
            if (event.target.checked) {
                if (applicationCount > 0) {
                    setPublishDialogOpen(true);
                } else {
                    dispatch(publishAudition(audition?._id));
                }
            } else {
                if (applicationCount > 0) {
                    setUnpublishDialogOpen(true);
                } else {
                    dispatch(unPublishAudition(audition?._id));
                }
            }
        },
        [applications?.length, audition?._id, dispatch],
    );

    const renderPublishedStatus = useMemo(() => {
        return (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                {audition && (
                    <LinkBox
                        link={generateShareLink('audition', audition, true)}
                        customComponent={
                            <CustomizedSwitches
                                switchProps={{
                                    checked: audition?.status === 'published',
                                    onChange: handlePublishStatusChange,
                                }}
                                label={audition?.status === 'published' ? 'Published' : 'Draft'}
                                style={{
                                    color: theme[themeStyle].primary_white,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                }}
                            />
                        }
                    />
                )}
            </Box>
        );
    }, [handlePublishStatusChange, themeStyle, audition]);

    const handleSortChange = (event: SelectChangeEvent<string>) => {
        onSortOptionChange(event.target.value);
    };

    const handleRoleChange = (event: SelectChangeEvent<string>) => {
        onRoleFilterChange(event.target.value);
    };

    const tabs = [
        { value: 'applications', label: 'Applications' },
        { value: 'details', label: 'Details' },
        { value: 'analytics', label: 'Analytics' },
        { value: 'promotions', label: 'Promotions' },
    ];

    const handleTabChange = (tabId: string) => {
        setActiveTab(tabId);
        setSearchParams({ tab: tabId });
    };

    // const handleEditRolesClick = () => {
    //     setRoleDialogOpen(true);
    // };

    const handleRoleSave = (role: Partial<IRole>) => {
        if (selectedRole && selectedRole._id) {
            // Update existing role
            dispatch(
                updateRole({
                    id: selectedRole._id!,
                    body: {
                        ...selectedRole,
                        ...role,
                        role_type: (role.role_type as FormOption) || (selectedRole.role_type as string),
                        is_paid:
                            (Number(role.compensation) && Number(role.compensation) > 0) ||
                            Number(selectedRole.compensation) > 0
                                ? 'paid'
                                : 'unpaid',
                        character: role.character || selectedRole.character,
                        description: role.description || selectedRole.description,
                        gender: role.gender || selectedRole.gender,
                        age_range: [role.min_age || selectedRole.min_age, role.max_age || selectedRole.max_age].join(
                            '-',
                        ),
                        expertise: role.expertise || selectedRole.expertise,
                        required_media: role.required_media || selectedRole.required_media,
                        currency: role.currency || selectedRole.currency,
                        compensation: role.compensation || selectedRole.compensation,
                        requires_subscription: role.requires_subscription || selectedRole.requires_subscription,
                        nudity_or_kissing: role.nudity_or_kissing || selectedRole.nudity_or_kissing,
                        min_age: role.min_age || selectedRole.min_age,
                        max_age: role.max_age || selectedRole.max_age,
                    },
                }),
            );
        } else if (audition?._id) {
            // Create new role
            const newRole: IRoleCreator = {
                role_type: role.role_type || { label: 'Cast', value: 'cast' },
                is_paid: role.compensation && Number(role.compensation) > 0 ? 'paid' : 'unpaid',
                character: role.character || '',
                description: role.description || '',
                gender: role.gender || '',
                age_range: [role.min_age || 0, role.max_age || 0].join('-'),
                expertise: role.expertise,
                required_media: role.required_media || [],
                currency: role.currency || 'USD',
                compensation: role.compensation || 0,
                requires_subscription: role.requires_subscription,
                min_age: role.min_age,
                max_age: role.max_age,
            };
            dispatch(
                createRole({
                    auditionId: audition._id,
                    body: newRole,
                }),
            );
        }
    };

    const deleteMessage =
        applications?.length > 0
            ? `${audition?.title} has ${applications?.length} applications. Are you sure you want to delete this?`
            : `${audition?.title} is currently live. Are you sure you want to delete this?`;

    const handleDeleteAudition = () => {
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = () => {
        if (audition?._id) {
            dispatch(deleteAudition(audition._id));
            navigate('/dashboard');
        }
        setDeleteDialogOpen(false);
    };

    if (!audition && loading && !applications) {
        return (
            <Container disableGutters maxWidth="mdLg" sx={{ py: 4 }}>
                <Typography>Loading...</Typography>
            </Container>
        );
    }

    if (error) {
        return (
            <Container
                disableGutters
                sx={{
                    py: 4,
                    height: '100vh',
                    display: 'flex',
                    width: '100vw',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                <Typography color="error">Error loading audition</Typography>
            </Container>
        );
    }

    return (
        <Container
            disableGutters
            maxWidth={false}
            sx={{
                py: 4,
                pr: { xs: 2, sm: 3, md: 4, xl: 3 },
                pl: { xs: 2, sm: 11, md: 12, xl: '130px' },
                width: '100%',
            }}>
            {/* Hero Section with Publish Toggle */}
            <Box sx={{ mb: 4 }}>
                {audition && (
                    <>
                        <CoverImageRenderer audition={audition} editMode onImageChange={onImageChange} />
                        {renderPublishedStatus}
                    </>
                )}
            </Box>

            {/* Roles Section */}
            <Box aria-labelledby="audition-container" sx={{ my: 4 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: '24px',
                            color: theme[themeStyle].tag_white,
                        }}>
                        Roles
                    </Typography>
                    <Stack direction="row" spacing={2}>
                        <CustomButton
                            label="Add New Role"
                            onClick={() => {
                                setSelectedRole(undefined);
                                setRoleDialogOpen(true);
                            }}
                            customStyle={{
                                borderRadius: 5,
                            }}
                        />
                    </Stack>
                </Stack>

                {audition && (
                    <RoleRenderer
                        audition={audition}
                        isManageMode
                        onManageClick={role => {
                            setSelectedRole(role);
                            setRoleDialogOpen(true);
                        }}
                    />
                )}
            </Box>

            {/* Tab Navigation */}
            <Box sx={{ my: 4 }}>
                <Stack direction={{ xs: 'column-reverse', sm: 'row' }} justifyContent={'space-between'}>
                    <SegmentedControl
                        options={tabs}
                        initialValue={activeTab}
                        onChange={handleTabChange}
                        fullWidth={!isMobile}
                        sx={{
                            mb: 3,
                            width: {
                                xs: 'auto',
                                sm: '100%',
                            },
                        }}
                    />
                </Stack>

                {activeTab === 'applications' ? (
                    <>
                        {/* Applications Section */}
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: '24px',
                                color: theme[themeStyle].tag_white,
                                mb: 2,
                            }}>
                            Applications
                        </Typography>

                        {/* Search and Filters */}
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            sx={{ mb: 2 }}>
                            <Box sx={{ width: { xs: '100%', sm: '40%' } }}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    placeholder="Search applicants..."
                                    value={value}
                                    onChange={handleSearchInputChange}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: theme[themeStyle].tag_white }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: value ? (
                                            <InputAdornment position="end">
                                                <img
                                                    src={close}
                                                    onClick={handleClearSearch}
                                                    alt="close icon"
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            </InputAdornment>
                                        ) : null,
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '50px',
                                            '& fieldset': {
                                                borderColor: theme[themeStyle].grey,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme[themeStyle].tag_white,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme[themeStyle].primary_orange,
                                            },
                                            '& .MuiInputBase-input': {
                                                color: theme[themeStyle].tag_white,
                                            },
                                        },
                                    }}
                                />
                            </Box>

                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={2}
                                sx={{
                                    width: { xs: '100%', sm: '60%' },
                                    justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                                    py: 2,
                                }}>
                                {/* Status Filter Dropdown */}
                                <StyledFormControl
                                    size="small"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: theme[themeStyle].tag_white,
                                            '& fieldset': {
                                                borderColor: theme[themeStyle].grey,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme[themeStyle].tag_white,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme[themeStyle].primary_orange,
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme[themeStyle].tag_white,
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].tag_white,
                                        },
                                        width: '100%',
                                        ml: -0.1,
                                    }}>
                                    <InputLabel id="status-filter-label">
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <FilterListIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                            Status
                                        </Box>
                                    </InputLabel>
                                    <Select
                                        labelId="status-filter-label"
                                        value={filterStatus}
                                        onChange={handleStatusChange}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    backgroundColor: theme[themeStyle].gray_text,
                                                    color: theme[themeStyle].primary_white,
                                                    '& .MuiMenuItem-root': {
                                                        color: theme[themeStyle].primary_white,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].background_gradient.end,
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                            color: theme[themeStyle].black,
                                                            '&:hover': {
                                                                backgroundColor: theme[themeStyle].primary_orange,
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                        }}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <FilterListIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                                Status
                                            </Box>
                                        }>
                                        {STATUS_OPTIONS.map((option, index) => (
                                            <MenuItem key={index} value={option.value}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        fontSize: '16px',
                                                        color: theme[themeStyle].tag_white,
                                                    }}>
                                                    {option.label}
                                                </Typography>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </StyledFormControl>

                                {/* Role Filter Dropdown */}
                                <StyledFormControl
                                    size="small"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: theme[themeStyle].tag_white,
                                            '& fieldset': {
                                                borderColor: theme[themeStyle].grey,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme[themeStyle].tag_white,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme[themeStyle].primary_orange,
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme[themeStyle].tag_white,
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].tag_white,
                                        },
                                    }}>
                                    <InputLabel id="role-filter-label">Role</InputLabel>
                                    <Select
                                        labelId="role-filter-label"
                                        value={roleFilter}
                                        onChange={handleRoleChange}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    backgroundColor: theme[themeStyle].gray_text,
                                                    color: theme[themeStyle].primary_white,
                                                    '& .MuiMenuItem-root': {
                                                        color: theme[themeStyle].primary_white,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].background_gradient.end,
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                            color: theme[themeStyle].black,
                                                            '&:hover': {
                                                                backgroundColor: theme[themeStyle].primary_orange,
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                        }}
                                        label="Role">
                                        {roleOptions.map((role, index) => (
                                            <MenuItem key={index} value={role.id}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        fontSize: '16px',
                                                        color: theme[themeStyle].tag_white,
                                                    }}>
                                                    {role.name}
                                                </Typography>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </StyledFormControl>

                                {/* Sort Dropdown */}
                                <StyledFormControl
                                    size="small"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: theme[themeStyle].tag_white,
                                            '& fieldset': {
                                                borderColor: theme[themeStyle].grey,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme[themeStyle].tag_white,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme[themeStyle].primary_orange,
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme[themeStyle].tag_white,
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].tag_white,
                                        },
                                    }}>
                                    <InputLabel id="sort-label">
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <SortIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                            Sort
                                        </Box>
                                    </InputLabel>
                                    <Select
                                        labelId="sort-label"
                                        value={sortOption}
                                        onChange={handleSortChange}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    backgroundColor: theme[themeStyle].gray_text,
                                                    color: theme[themeStyle].primary_white,
                                                    '& .MuiMenuItem-root': {
                                                        color: theme[themeStyle].primary_white,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].background_gradient.end,
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                            color: theme[themeStyle].black,
                                                            '&:hover': {
                                                                backgroundColor: theme[themeStyle].primary_orange,
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                        }}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <SortIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                                Sort
                                            </Box>
                                        }>
                                        {SORT_OPTIONS.map((option, i) => (
                                            <MenuItem key={i} value={option.value}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        fontSize: '16px',
                                                        color: theme[themeStyle].tag_white,
                                                    }}>
                                                    {option.label}
                                                </Typography>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </StyledFormControl>
                            </Stack>
                        </Stack>

                        {/* Applications Grid */}
                        <Box sx={{ mt: 3, mb: 2, position: 'relative' }}>
                            {searchLoading && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'flex-start',
                                        zIndex: 2,
                                        borderRadius: 2,
                                    }}>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: '18px',
                                            mt: 10,
                                        }}>
                                        Searching...
                                    </Typography>
                                </Box>
                            )}
                            <Grid
                                container
                                spacing={3}
                                sx={{
                                    opacity: searchLoading ? 0.4 : 1,
                                    transition: 'opacity 0.2s ease-in-out',
                                }}>
                                {applications && applications.length > 0
                                    ? applications.map(application => (
                                          <Grid item xs={12} sm={6} md={4} key={application._id}>
                                              <ApplicationCard
                                                  application={application}
                                                  onViewDetailsClick={app => onViewDetailsClick(application)}
                                                  onAcceptClick={() =>
                                                      onAcceptApplication && onAcceptApplication(application)
                                                  }
                                                  onRejectClick={() =>
                                                      onRejectApplication && onRejectApplication(application)
                                                  }
                                                  onMessageClick={() =>
                                                      onMessageApplicant && onMessageApplicant(application)
                                                  }
                                                  onArchiveClick={() =>
                                                      onArchiveApplication && onArchiveApplication(application)
                                                  }
                                                  onCastClick={() => onCastApplicant && onCastApplicant(application)}
                                              />
                                          </Grid>
                                      ))
                                    : !searchLoading && (
                                          <Grid item xs={12}>
                                              <Box
                                                  sx={{
                                                      p: 4,
                                                      textAlign: 'center',
                                                      border: `1px dashed ${theme[themeStyle].grey}`,
                                                      borderRadius: 2,
                                                  }}>
                                                  <Typography
                                                      sx={{
                                                          color: theme[themeStyle].tag_white,
                                                          fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                          fontSize: '16px',
                                                      }}>
                                                      No applications found
                                                  </Typography>
                                              </Box>
                                          </Grid>
                                      )}
                            </Grid>
                        </Box>

                        {/* Pagination */}
                        {applications && applications.length > 0 && !searchLoading && (
                            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                                <CustomPagination
                                    pagination={pagination}
                                    rowsPerPageOptions={[6, 12, 24]}
                                    onRowsChange={onRowsChange}
                                    onPageChange={onPageChange}
                                />
                            </Box>
                        )}
                    </>
                ) : activeTab === 'details' ? (
                    audition && (
                        <AuditionDetailsTab
                            audition={audition}
                            onEditDetails={section => {
                                // Handle edit details if needed
                                console.log('Editing section:', section);
                            }}
                        />
                    )
                ) : activeTab === 'analytics' ? (
                    <AuditionAnalyticsTab />
                ) : (
                    <AuditionPromotionsTab />
                )}
            </Box>

            {/* Danger Zone */}
            <Box
                sx={{
                    mt: 4,
                    p: 3,
                    borderRadius: 2,
                    border: `1px solid ${theme[themeStyle].primary_red}`,
                    backgroundColor: theme[themeStyle].deep_grey,
                }}>
                <Typography
                    sx={{
                        color: theme[themeStyle].primary_red,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: '1.2rem',
                        mb: 2,
                    }}>
                    Danger Zone
                </Typography>
                <Typography
                    sx={{
                        color: theme[themeStyle].primary_white,
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                        mb: 3,
                    }}>
                    {applications?.length ? (
                        <>
                            This audition has {applications.length}{' '}
                            {applications.length === 1 ? 'application' : 'applications'}. Deleting this audition will
                            remove all associated applications. This action cannot be undone.
                        </>
                    ) : (
                        'Once you delete an audition, this action cannot be undone. Please be certain.'
                    )}
                </Typography>
                <CustomButton
                    label="Delete Audition"
                    onClick={handleDeleteAudition}
                    customStyle={{
                        backgroundColor: theme[themeStyle].primary_red,
                        opacity: 1,
                        borderRadius: '5px',
                    }}
                    sx={{
                        '&:hover': {
                            backgroundColor: theme[themeStyle].primary_red,
                            opacity: 0.8,
                        },
                    }}
                />
            </Box>

            {/* Role Management Dialog */}
            <RoleManagementDialog
                open={roleDialogOpen}
                role={selectedRole}
                onClose={() => {
                    setRoleDialogOpen(false);
                    setSelectedRole(undefined);
                }}
                onSave={handleRoleSave}
            />

            {/* Delete Confirmation Dialog */}
            <DialogueSheet
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onAgreeClicked={handleConfirmDelete}
                onDisagreeClick={() => setDeleteDialogOpen(false)}
                header="Delete Audition"
                body={deleteMessage}
                positiveText="Delete"
                negativeText="Cancel"
                negtiveTextStyle={{
                    color: theme[themeStyle].tag_white,
                }}
            />

            {/* Publish Confirmation Dialog */}
            <DialogueSheet
                open={publishDialogOpen}
                onClose={() => setPublishDialogOpen(false)}
                onAgreeClicked={() => {
                    dispatch(publishAudition(audition?._id));
                    setPublishDialogOpen(false);
                }}
                onDisagreeClick={() => setPublishDialogOpen(false)}
                header="Publish Audition"
                body={`This audition has ${
                    applications?.length || 0
                } applications. Are you sure you want to publish it?`}
                positiveText="Publish"
                negativeText="Cancel"
                negtiveTextStyle={{
                    color: theme[themeStyle].tag_white,
                }}
            />

            {/* Unpublish Confirmation Dialog */}
            <DialogueSheet
                open={unpublishDialogOpen}
                onClose={() => setUnpublishDialogOpen(false)}
                onAgreeClicked={() => {
                    dispatch(unPublishAudition(audition?._id));
                    setUnpublishDialogOpen(false);
                }}
                onDisagreeClick={() => setUnpublishDialogOpen(false)}
                header="Unpublish Audition"
                body={`This audition has ${
                    applications?.length || 0
                } applications. Are you sure you want to unpublish it?`}
                positiveText="Unpublish"
                negativeText="Cancel"
                negtiveTextStyle={{
                    color: theme[themeStyle].tag_white,
                }}
            />
        </Container>
    );
};

export { ManageAuditionContainer };
